import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services';
import LogoutButton from '../LogoutButton';

const ParpDocsPage: React.FC = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [nextParpDocsId, setNextParpDocsId] = useState<string>('0');
    const [date, setDate] = useState<Dayjs | null>(dayjs());
    const [sheetNames, setSheetNames] = useState<string[]>([]);
    const [selectedSheet, setSelectedSheet] = useState<string>('');
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    useEffect(() => {
        if (!localStorage.getItem('token')) {
            navigate('/login');
        }
    }, [navigate]);

    const fetchNextParpDocsId = async () => {
        try {
            setIsLoading(true);
            const id = await api.getLastParpDocsId();
            setNextParpDocsId((id + 1).toString());
        } catch (error) {
            console.error("Error fetching next PARP docs ID:", error);
            enqueueSnackbar('Błąd podczas pobierania następnego ID dokumentów PARP', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSheetNames = async () => {
        try {
            setIsLoading(true);
            const names = await api.getParpSheetNames();
            setSheetNames(names);
            if (names.length > 0 && !selectedSheet) {
                setSelectedSheet(names[0]);
            }
        } catch (error) {
            console.error("Error fetching sheet names:", error);
            enqueueSnackbar('Błąd podczas pobierania listy arkuszy', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (localStorage.getItem('token')) {
            fetchNextParpDocsId();
            fetchSheetNames();
        }
    }, [enqueueSnackbar]);

    const handleDownload = async () => {
        const parsedId = parseInt(nextParpDocsId, 10);
        if (isNaN(parsedId) || parsedId < 1) {
            enqueueSnackbar('Proszę podać prawidłową liczbę większą od 0', { variant: 'error' });
            return;
        }

        if (!date) {
            enqueueSnackbar('Proszę wybrać datę', { variant: 'error' });
            return;
        }

        if (!selectedSheet) {
            enqueueSnackbar('Proszę wybrać arkusz', { variant: 'error' });
            return;
        }

        try {
            setIsLoading(true);
            enqueueSnackbar('Rozpoczęto proces generowania dokumentów PARP', { variant: 'info' });
            await api.downloadParpDocs(parsedId, date.format('YYYY-MM-DD'), selectedSheet);
            enqueueSnackbar('Dokumenty PARP pobrane pomyślnie', { variant: 'success' });
            await fetchNextParpDocsId();
        } catch (error) {
            console.error("Error downloading PARP docs:", error);
            enqueueSnackbar('Błąd podczas pobierania dokumentów PARP', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ padding: 2, maxWidth: 1200, margin: 'auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
                <TextField
                    label="Następne ID dokumentów PARP"
                    type="text"
                    value={nextParpDocsId}
                    onChange={(e) => setNextParpDocsId(e.target.value)}
                    disabled={isLoading}
                    sx={{ minWidth: 300 }}
                />
                <DatePicker
                    label="Data"
                    value={date}
                    onChange={(newValue) => setDate(newValue)}
                    disabled={isLoading}
                    sx={{ minWidth: 300 }}
                />
                <FormControl sx={{ minWidth: 300 }} disabled={isLoading}>
                    <InputLabel id="sheet-select-label">Arkusz</InputLabel>
                    <Select
                        labelId="sheet-select-label"
                        value={selectedSheet}
                        label="Arkusz"
                        onChange={(e) => setSelectedSheet(e.target.value)}
                    >
                        {sheetNames.map((name) => (
                            <MenuItem key={name} value={name}>
                                {name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleDownload}
                    disabled={isLoading}
                >
                    Pobierz dokumenty PARP
                </Button>
            </Box>
            <LogoutButton />
        </Box>
    );
};

export default ParpDocsPage;

